import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2, LogOut, Menu, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import AdminSidebar from '../../components/AdminSidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    original_price: '',
    discount_price: '',
    stock: '',
    features: '',
    images: ''
  });

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (isLoggedIn !== 'true') {
      navigate('/admin', { replace: true });
      return;
    }
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_username');
    navigate('/admin', { replace: true });
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setUploadedImages(product.images || []);
      setFormData({
        name: product.name,
        description: product.description,
        original_price: product.original_price,
        discount_price: product.discount_price,
        stock: product.stock,
        features: product.features.join('\n'),
        images: product.images.join('\n')
      });
    } else {
      setEditingProduct(null);
      setUploadedImages([]);
      setFormData({
        name: '',
        description: '',
        original_price: '',
        discount_price: '',
        stock: '',
        features: '',
        images: ''
      });
    }
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const formDataUpload = new FormData();
    
    for (let i = 0; i < files.length; i++) {
      formDataUpload.append('files', files[i]);
    }

    try {
      const response = await axios.post(`${API_URL}/api/upload/images`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const newUrls = response.data.urls.map(url => `${API_URL}${url}`);
      const allImages = [...uploadedImages, ...newUrls];
      setUploadedImages(allImages);
      setFormData(prev => ({ ...prev, images: allImages.join('\n') }));
      toast.success(`${response.data.count} image(s) uploaded successfully`);
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (indexToRemove) => {
    const newImages = uploadedImages.filter((_, index) => index !== indexToRemove);
    setUploadedImages(newImages);
    setFormData(prev => ({ ...prev, images: newImages.join('\n') }));
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();

    const productData = {
      name: formData.name,
      description: formData.description,
      original_price: parseFloat(formData.original_price),
      discount_price: parseFloat(formData.discount_price),
      stock: parseInt(formData.stock),
      features: formData.features.split('\n').filter(f => f.trim()),
      images: formData.images.split('\n').filter(i => i.trim())
    };

    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/api/products/${editingProduct.id}`, productData);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API_URL}/api/products`, productData);
        toast.success('Product created successfully');
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Failed to save product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API_URL}/api/products/${productId}`);
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F1F5F9]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#064E3B]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <AdminSidebar active="products" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="admin-content">
        <div className="admin-topbar">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden"
            >
              <Menu className="w-6 h-6" />
            </Button>
            <h1 className="text-xl font-bold text-[#0f172a]" data-testid="products-title">Products Management</h1>
          </div>
          <div className="flex items-center gap-3">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-product-button"
                  onClick={() => handleOpenDialog()}
                  className="bg-[#064E3B] hover:bg-[#065F46] text-white"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Product
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveProduct} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Product Name *</Label>
                    <Input
                      id="name"
                      data-testid="product-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      data-testid="product-description-input"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="original_price">Original Price *</Label>
                      <Input
                        id="original_price"
                        type="number"
                        data-testid="product-original-price-input"
                        value={formData.original_price}
                        onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="discount_price">Discount Price *</Label>
                      <Input
                        id="discount_price"
                        type="number"
                        data-testid="product-discount-price-input"
                        value={formData.discount_price}
                        onChange={(e) => setFormData({ ...formData, discount_price: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="stock">Stock Quantity *</Label>
                    <Input
                      id="stock"
                      type="number"
                      data-testid="product-stock-input"
                      value={formData.stock}
                      onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="features">Features (one per line)</Label>
                    <Textarea
                      id="features"
                      data-testid="product-features-input"
                      value={formData.features}
                      onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                      rows={4}
                      placeholder="Feature 1\nFeature 2\nFeature 3"
                    />
                  </div>
                  <div>
                    <Label>Product Images *</Label>
                    <div className="mt-2 space-y-3">
                      {/* Image Upload Button */}
                      <div 
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-zinc-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#064E3B] transition-colors"
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <Upload className="w-8 h-8 mx-auto text-zinc-400 mb-2" />
                        <p className="text-sm text-zinc-600">
                          {uploading ? 'Uploading...' : 'Click to upload images'}
                        </p>
                        <p className="text-xs text-zinc-400 mt-1">
                          JPEG, PNG, WebP, GIF supported
                        </p>
                      </div>
                      
                      {/* Uploaded Images Preview */}
                      {uploadedImages.length > 0 && (
                        <div className="grid grid-cols-4 gap-2">
                          {uploadedImages.map((url, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={url}
                                alt={`Product ${index + 1}`}
                                className="w-full h-20 object-cover rounded-lg border"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Manual URL Input */}
                      <div>
                        <Label htmlFor="images" className="text-xs text-zinc-500">Or paste image URLs (one per line)</Label>
                        <Textarea
                          id="images"
                          data-testid="product-images-input"
                          value={formData.images}
                          onChange={(e) => {
                            setFormData({ ...formData, images: e.target.value });
                            setUploadedImages(e.target.value.split('\n').filter(i => i.trim()));
                          }}
                          rows={2}
                          placeholder="https://example.com/image1.jpg"
                          className="text-xs"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      data-testid="save-product-button"
                      className="flex-1 bg-[#064E3B] hover:bg-[#065F46]"
                    >
                      {editingProduct ? 'Update Product' : 'Create Product'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="ghost"
              className="text-zinc-600"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <div className="p-6">
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="admin-table" data-testid="products-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Product Name</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Discount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} data-testid={`product-row-${product.id}`}>
                      <td>
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      </td>
                      <td>
                        <p className="font-semibold text-[#0f172a]">{product.name}</p>
                        <p className="text-sm text-zinc-500">{product.id}</p>
                      </td>
                      <td>
                        <p className="font-semibold text-[#064E3B]">₹{product.discount_price}</p>
                        <p className="text-sm text-zinc-400 line-through">₹{product.original_price}</p>
                      </td>
                      <td>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                          product.stock > 10
                            ? 'bg-green-100 text-green-800'
                            : product.stock > 0
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {product.stock} units
                        </span>
                      </td>
                      <td>
                        <span className="inline-flex px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-semibold">
                          {product.discount_percentage}% OFF
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <Button
                            data-testid={`edit-product-${product.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(product)}
                            className="text-[#064E3B] hover:bg-[#F0FDF4]"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`delete-product-${product.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteProduct(product.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
